import type { Tower } from "../entities/Tower.ts";
import { ELEMENTS, type Element } from "../entities/elements.ts";
import {
  drawMalwareNode,
  drawFirewallNode,
  drawEmpNode,
  drawSignalNode,
  dotP,
  TAU,
} from "./TowerNodeShapes.ts";
import { glowCache } from "./GlowSpriteCache.ts";
import { ELEMENT_COLORS, ELEMENT_PARTICLES, ELEMENT_RAMP } from "./designTokens.ts";
import { emissiveFill, mixHex } from "./EmissiveMaterial.ts";
import { arenaMotionScale } from "../engine/MotionSettings.ts";

// ─── Draw Function Types ───────────────────────────────

export type NodeDrawFn = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  tower: Tower,
  time: number,
) => void;

export type IconDrawFn = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
) => void;

// ─── Element Definition ────────────────────────────────

export interface ElementDef {
  label: string;
  role: string;
  particles: { colors: string[]; shape: string };
  drawNode: NodeDrawFn;
  drawIcon: IconDrawFn;
}

// ─── Icon Draw Functions (extracted from TowerSelector) ─

function drawFireIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  _color: string,
): void {
  ctx.beginPath();
  ctx.moveTo(cx, cy - 14);
  ctx.lineTo(cx + 4, cy - 4);
  ctx.lineTo(cx + 14, cy);
  ctx.lineTo(cx + 4, cy + 4);
  ctx.lineTo(cx, cy + 14);
  ctx.lineTo(cx - 4, cy + 4);
  ctx.lineTo(cx - 14, cy);
  ctx.lineTo(cx - 4, cy - 4);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

function drawWaterIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  _color: string,
): void {
  ctx.beginPath();
  for (let a = 0; a < 6; a++) {
    const angle = (Math.PI / 3) * a - Math.PI / 6;
    const x = cx + 12 * Math.cos(angle);
    const y = cy + 12 * Math.sin(angle);
    a === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

function drawLightningIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
): void {
  ctx.setLineDash([4, 3]);
  ctx.beginPath();
  ctx.arc(cx, cy, 12, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(cx, cy, 4, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}

function drawSignalIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
): void {
  for (let r = 6; r <= 14; r += 4) {
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.arc(cx, cy, 2, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}

function drawKineticIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx - 12, cy);
  ctx.lineTo(cx + 12, cy);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx + 8, cy, 3, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 8, cy - 5);
  ctx.lineTo(cx - 2, cy - 5);
  ctx.moveTo(cx - 8, cy + 5);
  ctx.lineTo(cx - 2, cy + 5);
  ctx.stroke();
}

function drawVirusIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, 8, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = "#8A2BE2";
  ctx.lineWidth = 1.4;
  for (let i = 0; i < 6; i++) {
    const angle = i * (Math.PI / 3) + Math.PI / 6;
    const x1 = cx + Math.cos(angle) * 2;
    const y1 = cy + Math.sin(angle) * 2;
    const x2 = cx + Math.cos(angle) * 7;
    const y2 = cy + Math.sin(angle) * 7;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
}

// ─── Rail-Sentinel body (kinetic) ──────────────────────

/** Deterministic [-1,1] pseudo-noise from a scalar seed. Render-only jitter for
 *  the charge crackle — no Math.random, so the look is stable frame-to-frame and
 *  sim determinism is never at risk. */
function railNoise(seed: number): number {
  const s = Math.sin(seed * 12.9898) * 43758.5453;
  return (s - Math.floor(s)) * 2 - 1;
}

/** Rail-Sentinel (kinetic) — "Capacitor Twin-Rail" railgun.
 *
 *  The slow 1500ms charge is the premium beat made legible: capacitor cells fill
 *  sequentially, the core focuses (steel→white) and a bead races to the muzzle,
 *  then a crisp discharge. Charge stays TIGHT — no expanding halo — per the
 *  kinetic art-direction ("mass, not energy / minimal halo").
 *
 *  Division of labour: the recoil KICK, muzzle flash and rail tracer are owned by
 *  TowerRenderer (drawn around / after this call) — this draws the BODY only, so
 *  nothing double-draws. All animated flair damps with arenaMotionScale() (BL-44
 *  load governor + BL-45 reduced-motion); at scale 0 the charge still reads via
 *  static cell-fill + core brightness (accessible).
 *
 *  Tier expresses MASS, not size: rail length, rung density and capacitor count
 *  grow; L3 adds penetrator fins (armor-pierce), L4 the pierce-corridor guide,
 *  L5 stacks a second rail pair (double-shot). */
function drawKineticNode(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  tower: Tower,
  time: number,
): void {
  const tier = Math.max(1, Math.min(6, tower.level || 1));
  const m = arenaMotionScale(); // 0 = reduced / heavy load, 1 = full motion
  const steel = tower.color;
  const light = tower.colorLight;
  const ramp = ELEMENT_RAMP[tower.element];

  // Charge progress p ∈ [0,1] (only while charging). Denominator = the ACTUAL
  // charge duration captured on entry (sniperChargeDuration) so the designated-
  // synergy half-charge still fills 0→1 correctly; falls back to chargeTime.
  const charging = tower.sniperState === "charging";
  const dur =
    tower.sniperChargeDuration > 0
      ? tower.sniperChargeDuration
      : (tower.special?.chargeTime ?? 1500);
  const p = charging
    ? Math.max(0, Math.min(1, 1 - tower.sniperStateTimer / dur))
    : 0;
  // Discharge flash on the capacitor cells, reusing the recoil clock (set to 80
  // on fire in SniperCombat). 1 → 0 over 80ms.
  const fireFlash = Math.max(0, Math.min(1, tower.fireCooldownVisual / 80));
  // Faint "armed" idle breath on the core (motion-gated) so a resting sentinel
  // feels alive without adding visual noise.
  const idleBreath =
    !charging && fireFlash <= 0
      ? (0.06 + 0.05 * Math.sin(time * 4 + cx * 0.05)) * m
      : 0;

  const railLen = size * (0.4 + tier * 0.018);
  const railGap = size * 0.085;
  const breech = -railLen * 0.78;
  const capCells = 4 + tier;
  const rungs = 2 + tier * 2;
  const baseR = size * 0.2;

  // ── base platform: lit octagon dome + dark rim (reads as mass / metal) ──
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
    const x = cx + Math.cos(a) * baseR;
    const y = cy + Math.sin(a) * baseR;
    if (i) ctx.lineTo(x, y);
    else ctx.moveTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = emissiveFill(ctx, cx, cy, baseR, ramp);
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = ramp.rim;
  ctx.stroke();

  // ── capacitor cells: fill sequentially with charge; flash + drain on fire ──
  const litF = capCells * p;
  for (let i = 0; i < capCells; i++) {
    const a = (i / capCells) * Math.PI * 2 - Math.PI / 2;
    const rx = cx + Math.cos(a) * (baseR * 0.78);
    const ry = cy + Math.sin(a) * (baseR * 0.78);
    const lvl = Math.max(Math.max(0, Math.min(1, litF - i)), fireFlash);
    ctx.save();
    ctx.translate(rx, ry);
    ctx.rotate(a);
    ctx.fillStyle = lvl > 0.02 ? mixHex("#3a4452", "#ffffff", lvl) : "#2b333d";
    ctx.fillRect(-size * 0.035, -size * 0.0225, size * 0.07, size * 0.045);
    ctx.restore();
  }

  // ── rotating rail assembly (aim eased into tower.barrelAngle by SniperCombat) ──
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(tower.barrelAngle);
  ctx.lineCap = "round";

  const coreCol = mixHex(steel, "#ffffff", p);
  const coreA = 0.14 + 0.72 * p + idleBreath;

  const drawRailPair = (yoff: number, lenScale: number): number => {
    const mz = railLen * lenScale;

    // two rails
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = steel;
    for (const sgn of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(breech, yoff + sgn * railGap);
      ctx.lineTo(mz, yoff + sgn * railGap);
      ctx.stroke();
    }

    // rungs between the rails
    ctx.lineWidth = 1;
    ctx.strokeStyle = mixHex(steel, "#6c7a8c", 0.4);
    for (let i = 0; i < rungs; i++) {
      const x = breech + ((i + 0.5) / rungs) * (mz - breech);
      ctx.beginPath();
      ctx.moveTo(x, yoff - railGap);
      ctx.lineTo(x, yoff + railGap);
      ctx.stroke();
    }

    // focusing core line — soft underlay + bright core (cheap glow, no shadowBlur)
    ctx.strokeStyle = coreCol;
    ctx.globalAlpha = Math.min(0.5, coreA * 0.5);
    ctx.lineWidth = 2 + 3 * p;
    ctx.beginPath();
    ctx.moveTo(breech + 2, yoff);
    ctx.lineTo(mz - 2, yoff);
    ctx.stroke();
    ctx.globalAlpha = Math.min(1, coreA);
    ctx.lineWidth = 1 + 1.5 * p;
    ctx.beginPath();
    ctx.moveTo(breech + 2, yoff);
    ctx.lineTo(mz - 2, yoff);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // charge bead — last 40% of charge, racing breech→muzzle (motion-gated)
    if (charging && p > 0.6 && m > 0) {
      const bp = (p - 0.6) / 0.4;
      const x0 = breech + 4;
      const bx = x0 + (mz - 4 - x0) * bp;
      ctx.fillStyle = light;
      ctx.globalAlpha = 0.5 * m;
      ctx.beginPath();
      ctx.arc(bx, yoff, (2.4 + 1.6 * bp) * 1.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(bx, yoff, 1.6 + 1.2 * bp, 0, Math.PI * 2);
      ctx.fill();
    }

    // rail arcs — tiny CONTAINED crackles past 55% charge (motion-gated, ≤3)
    if (charging && p > 0.55 && m >= 0.5) {
      const n = Math.floor(((p - 0.55) / 0.45) * 3) + 1;
      const tick = Math.floor(time * 30);
      ctx.strokeStyle = "#eef4fb";
      ctx.lineWidth = 0.8;
      ctx.globalAlpha = (0.45 + 0.35 * (0.5 + 0.5 * railNoise(tick))) * m;
      for (let k = 0; k < n; k++) {
        const ax = mz * (0.4 + 0.52 * (0.5 + 0.5 * railNoise(tick + k * 7)));
        ctx.beginPath();
        ctx.moveTo(ax, yoff - railGap);
        ctx.lineTo(
          ax + railNoise(tick + k * 13) * 3,
          yoff + railNoise(tick + k * 5) * railGap * 0.6,
        );
        ctx.lineTo(ax + railNoise(tick + k * 17) * 3, yoff + railGap);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // muzzle aperture + charge hot-core (grows with charge; the FIRE burst is
    // TowerRenderer's muzzle flash, so these never overlap)
    ctx.strokeStyle = steel;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(mz, yoff, size * 0.05, 0, Math.PI * 2);
    ctx.stroke();
    const hot = p * p;
    if (hot > 0.02) {
      ctx.fillStyle = light;
      ctx.globalAlpha = 0.35 * hot;
      ctx.beginPath();
      ctx.arc(mz, yoff, size * (0.04 + 0.06 * hot), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = Math.min(1, hot);
      ctx.fillStyle = mixHex(light, "#ffffff", hot);
      ctx.beginPath();
      ctx.arc(mz, yoff, size * (0.02 + 0.03 * hot), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // penetrator fins (tier ≥ 3 = armor-pierce)
    if (tier >= 3) {
      ctx.strokeStyle = steel;
      ctx.lineWidth = 1.3;
      for (let i = 0; i < tier - 2; i++) {
        const span = railGap * (1.15 + i * 0.3);
        const x = mz - 5 - i * (size * 0.05);
        ctx.beginPath();
        ctx.moveTo(x, yoff - span);
        ctx.lineTo(x + size * 0.09, yoff);
        ctx.lineTo(x, yoff + span);
        ctx.stroke();
      }
    }

    return mz;
  };

  // pierce-corridor guide (tier ≥ 4) — faint dashed outriggers, brighter on charge
  if (tier >= 4) {
    ctx.strokeStyle = steel;
    ctx.globalAlpha = 0.22 + 0.25 * p;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 4]);
    for (const sgn of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(railLen, sgn * railGap * 2.1);
      ctx.lineTo(railLen + size * 0.4, sgn * railGap * 2.1);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }

  // L5 double-shot → stacked over/under rail pair; else a single centred pair
  if (tier >= 5) {
    drawRailPair(-railGap * 1.7, 0.92);
    drawRailPair(railGap * 1.7, 0.92);
  } else {
    drawRailPair(0, 1);
  }

  ctx.restore();
}

// ─── The Registry ──────────────────────────────────────

export const ELEMENT_REGISTRY: Record<Element, ElementDef> = {
  fire: {
    label: "MALWARE",
    role: "DOT / Damage-over-Time",
    particles: { colors: [...ELEMENT_PARTICLES.fire], shape: "binary" },
    drawNode: drawMalwareNode,
    drawIcon: drawFireIcon,
  },
  water: {
    label: "ICE",
    role: "CHAIN / Multi-target",
    particles: { colors: [...ELEMENT_PARTICLES.water], shape: "binary" },
    drawNode: (ctx, cx, cy, size, tower, time) => {
      void time;
      drawFirewallNode(ctx, cx, cy, size, tower);
    },
    drawIcon: drawWaterIcon,
  },
  lightning: {
    label: "EMP",
    role: "BURST / Crit",
    particles: { colors: [...ELEMENT_PARTICLES.lightning], shape: "binary" },
    drawNode: drawEmpNode,
    drawIcon: drawLightningIcon,
  },
  signal: {
    label: "SIGNAL",
    role: "SUPPORT / Debuff (no direct damage)",
    particles: { colors: [...ELEMENT_PARTICLES.signal], shape: "binary" },
    drawNode: drawSignalNode,
    drawIcon: drawSignalIcon,
  },
  kinetic: {
    label: "Rail-Sentinel",
    role: "Precision Railgun",
    particles: { colors: [...ELEMENT_PARTICLES.kinetic], shape: "spark" },
    drawNode: drawKineticNode,
    drawIcon: drawKineticIcon,
  },
  virus: {
    label: "Contagion-Node",
    role: "Lobbed AoE Infection",
    particles: { colors: [...ELEMENT_PARTICLES.virus], shape: "binary" },
    // Virus / Contagion-Node (BL-176). A living capsid: an emissive mint core
    // ringed by knobbed receptor spikes (the virion corona — the unmistakable
    // "virus" silhouette), breathing a quiet, persistent infection pulse, with a
    // genetic nucleus surfacing at tier ≥ 3 (form, not just glow). The knobs ripen
    // toward the sanctioned toxic-violet edge (ART §5) — palette-only mint
    // identity, no drift toward the rose enemy family.
    drawNode: (ctx, cx, cy, size, tower, time) => {
      const ramp = ELEMENT_RAMP[tower.element];
      const tier = Math.max(1, Math.min(6, tower.level || 1));
      const m = arenaMotionScale();
      const mint = ELEMENT_COLORS.virus;
      const toxic = ELEMENT_PARTICLES.virus[2]; // sanctioned "toxic violet edge"
      const breath = 1 + Math.sin(time * 2.2 + cx * 0.02) * 0.06 * m;
      const coreR = size * 0.24 * breath;
      const receptors = 6 + tier * 2; // 8 → 16

      glowCache.draw(ctx, mint, coreR * 1.6, cx, cy, 0.4);

      // ── knobbed receptor spikes (the capsid corona — the virion read) ──
      ctx.strokeStyle = mint;
      ctx.lineWidth = 1.3;
      for (let i = 0; i < receptors; i++) {
        const ang = (i / receptors) * TAU + time * 0.22 * m;
        const tipR = coreR * 1.45 * (1 + Math.sin(time * 3 + i) * 0.05 * m);
        ctx.beginPath();
        ctx.moveTo(
          cx + Math.cos(ang) * coreR * 1.02,
          cy + Math.sin(ang) * coreR * 1.02,
        );
        ctx.lineTo(cx + Math.cos(ang) * tipR, cy + Math.sin(ang) * tipR);
        ctx.stroke();
        ctx.fillStyle = i % 3 === 0 ? toxic : tower.colorLight; // spike-protein knob
        dotP(ctx, cx, cy, ang, tipR, 1.5 + tier * 0.12);
      }

      // ── capsid body: emissive mint volume, hot center (BL-26/27) ──
      ctx.fillStyle = emissiveFill(ctx, cx, cy, coreR, ramp);
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, TAU);
      ctx.fill();

      // ── inner nucleus (tier geometry: a genetic hex core at T3+) ──
      if (tier >= 3) {
        ctx.strokeStyle = toxic;
        ctx.globalAlpha = 0.65;
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let i = 0; i <= 6; i++) {
          const a = (i / 6) * TAU + time * 0.4 * m;
          const rr = coreR * (i % 2 ? 0.3 : 0.5);
          const nx = cx + Math.cos(a) * rr;
          const ny = cy + Math.sin(a) * rr;
          if (i) ctx.lineTo(nx, ny);
          else ctx.moveTo(nx, ny);
        }
        ctx.closePath();
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // white-hot mint core (the bloom seed)
      ctx.fillStyle = ramp.core;
      dotP(ctx, cx, cy, 0, 0, coreR * 0.3);
    },
    drawIcon: drawVirusIcon,
  },
} satisfies Record<Element, ElementDef>;

// ─── Derived exports for backward compatibility ────────

/** Elements with a draw path. Derived from ELEMENT_REGISTRY keys. */
export const ELEMENT_DRAW: ReadonlySet<Element> = new Set(ELEMENTS);

/** Re-export ELEMENTS for consumers that imported TOWER_ELEMENTS. */
export { ELEMENTS, type Element } from "../entities/elements.ts";
